import { computed, inject, Injectable, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import {
  WheelGame,
  WheelBet,
  WheelRouletteEnd,
  WheelLoadRouletteBets,
  WheelColorsRecord,
  WheelAnimation,
  WheelLoadRouletteBetsList,
} from './interfaces/wheel.interface';
import { SocketService } from '@core/services/socket.service';
import { SessionService } from '@core/services/session.service';
import { sleep } from '@core/utils/sleep.util';
import {
  WHEEL_COLORS,
  WHEEL_MULTIPLIERS,
  WHEEL_SETTINGS,
} from './constants/colors.constants';

@Injectable({
  providedIn: 'root',
})
export class WheelService {
  private readonly MAX_HISTORY_ENTRIES = 100;
  private readonly socketService = inject(SocketService);
  private readonly session = inject(SessionService);
  private readonly toastrService = inject(ToastrService);

  readonly MIN_BET = 0.1;
  readonly MAX_BET = 650;

  history = signal<WheelGame[]>([]);
  last100Bets = computed<WheelColorsRecord>(() => {
    return this.history()
      .slice(0, 100)
      .reduce(
        (acc, round) => {
          const color = round.color;
          acc[color]++;
          return acc;
        },
        { gray: 0, red: 0, blue: 0, gold: 0 }
      );
  });

  betsList = signal<WheelLoadRouletteBetsList>({
    gray: [],
    red: [],
    blue: [],
    gold: [],
  });
  betsValue = computed<WheelColorsRecord>(() => {
    const { gray, red, blue, gold } = this.betsList();
    return {
      gray: gray.reduce((acc, bet) => acc + bet.amount, 0),
      red: red.reduce((acc, bet) => acc + bet.amount, 0),
      blue: blue.reduce((acc, bet) => acc + bet.amount, 0),
      gold: gold.reduce((acc, bet) => acc + bet.amount, 0),
    };
  });

  betAmount = signal<number>(this.MIN_BET);

  status = signal<WheelAnimation>('waiting');

  rollDate = signal<Date | null>(null);
  winningNumber = signal<number>(-1);
  winningColor = signal<string | null>(null);

  hash = signal<string | null>(null);
  gameid = signal<number | null>(null);

  readonly wheelColors = WHEEL_COLORS;
  readonly wheelSettings = WHEEL_SETTINGS;
  readonly wheelMultipliers = WHEEL_MULTIPLIERS;

  constructor() {
    this.addRouletteBet();
    this.rouletteStart();
    this.rouletteEnd();
    this.loadRouletteStats();
    this.loadRouletteBets();
  }

  private loadRouletteStats() {
    this.socketService.socket.on('load_roulette_stats', (data: WheelGame[]) => {
      const limitedHistory = [...data]
        .reverse()
        .slice(0, this.MAX_HISTORY_ENTRIES);
      this.history.set(limitedHistory);
    });
  }

  private loadRouletteBets() {
    this.socketService.socket.on(
      'load_roulette_bets',
      (data: WheelLoadRouletteBets) => {
        this.status.set('running');
        this.hash.set(data.hash);
        this.gameid.set(data.gameid);
        this.rollDate.set(
          new Date(data.starts * 1000 + this.wheelSettings.ROUND_DURATION)
        );
        this.betsList.set(data.bets);
      }
    );
  }

  private rouletteEnd() {
    this.socketService.socket.on(
      'roulette_end',
      async (data: WheelRouletteEnd) => {
        this.winningNumber.set(data.roll);
        this.status.set('rolling');
        await sleep(this.wheelSettings.ROLL_DURATION);
        this.winningColor.set(data.color);
        this.status.set('results');
        await sleep(this.wheelSettings.ROLL_RESULTS);

        const gameid = this.gameid();
        if (gameid) {
          const wheelGame: WheelGame = {
            gameid,
            color: data.color,
          };

          this.history.update((prev) => {
            const history = [wheelGame, ...prev];
            return history.slice(0, this.MAX_HISTORY_ENTRIES);
          });
        }
      }
    );
  }

  private rouletteStart() {
    this.socketService.socket.on(
      'roulette_start',
      (data: { hash: string; gameid: number; starts: number }) => {
        this.status.set('running');
        this.winningNumber.set(-1);
        this.hash.set(data.hash);
        this.gameid.set(data.gameid);
        this.rollDate.set(
          new Date(data.starts * 1000 + this.wheelSettings.ROUND_DURATION)
        );
        this.betsList.set({
          gray: [],
          red: [],
          blue: [],
          gold: [],
        });
        this.winningColor.set(null);
      }
    );
  }

  private addRouletteBet() {
    this.socketService.socket.on('add_roulette_bet', (data: WheelBet) => {
      this.betsList.update((prev) => {
        const colorList = prev[data.color];
        return {
          ...prev,
          [data.color]: [...colorList, data],
        };
      });
    });
  }

  public async bet(
    color: 'gray' | 'red' | 'blue' | 'gold' | string
  ): Promise<void> {
    if (this.betAmount() <= 0) {
      return;
    }

    const fixedAmount = Number(this.betAmount());

    const res = await this.socketService.emit('roulette_bet', {
      amount: fixedAmount,
      color: color,
      balance: this.session.balanceType(),
    });
    if (res) {
      this.toastrService.success(
        `Bet placed for <b>${fixedAmount.toFixed(
          2
        )}$</b> on <b>${color.toUpperCase()}</b>!`,
        'BET PLACED',
        { enableHtml: true }
      );
    }
  }
}
